import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";

const EMERGENT_AUTH_BASE = process.env.REACT_APP_EMERGENT_AUTH_URL || "https://auth.emergentagent.com";
const EMERGENT_AUTH_URL = (next) => `${EMERGENT_AUTH_BASE}/?redirect=${encodeURIComponent(next)}`;

export default function LoginPage() {
  const { user, login, register, googleSession } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("login");
  const [params] = useSearchParams();

  // V12.x — Permet aux CTA externes (ex: popup Bienvenue) d'ouvrir directement
  // l'onglet Inscription via `/connexion?tab=register`.
  useEffect(() => {
    const t = params.get("tab");
    if (t === "register" || t === "login") setTab(t);
  }, [params]);

  // V9.9 — Support de ?next= : après login/inscription/Google, redirige vers
  // la page d'origine plutôt que /dashboard par défaut. Anti-open-redirect :
  // on accepte uniquement les chemins internes (commençant par "/" et sans "//"
  // pour éviter les redirections vers https://attacker.example/).
  const rawNext = params.get("next") || "";
  const isSafePath = /^\/(?!\/)/.test(rawNext);
  // V9.19 UX Polish — Si aucun `next` explicite, les rôles COPF/Admin
  // atterrissent sur leur tableau de bord institutionnel plutôt que /dashboard.
  const defaultLanding = user && (user.roles || []).some((r) => ["admin", "copf"].includes(r)) ? "/copf" : "/dashboard";
  const safeNext = isSafePath ? rawNext : defaultLanding;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [gloading, setGloading] = useState(false);

  useEffect(() => { if (user) nav(safeNext, { replace: true }); }, [user, nav, safeNext]);

  // Handle Emergent Google callback hash
  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      if (sid) {
        setGloading(true);
        // V11.9 — On nettoie le hash IMMÉDIATEMENT pour éviter de re-déclencher
        // l'échange en cas de re-render (React StrictMode double-invoke).
        try { window.history.replaceState({}, "", window.location.pathname + window.location.search); } catch { /* ignore */ }
        googleSession(sid)
          .then(() => {
            toast.success("Connecté avec Google.");
            // La redirection se fera automatiquement via l'effect ci-dessus
            // dès que `user` est mis à jour (évite les race conditions).
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.error("[AITO.PF] Google auth failed", e);
            showError(e, { prefix: "Auth Google: " });
          })
          .finally(() => setGloading(false));
      }
    }
  }, [googleSession]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") await login(email, password);
      else await register(email, password, name || email.split("@")[0]);
      toast.success(tab === "login" ? "Bienvenue à bord." : "Compte créé. Bienvenue !");
      nav(safeNext, { replace: true });
    } catch (err) {
      showError(err);
    } finally { setLoading(false); }
  };

  const googleStart = () => {
    // V9.9 — Préserver le `next` dans le callback Emergent Google
    const callback = safeNext !== "/dashboard"
      ? `${window.location.origin}/connexion?next=${encodeURIComponent(safeNext)}`
      : `${window.location.origin}/connexion`;
    window.location.href = EMERGENT_AUTH_URL(callback);
  };

  return (
    <div className="mx-auto w-full max-w-md px-4 py-12">
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
          <h1 className="font-display text-2xl font-bold">Rejoins AITO.PF</h1>
        </div>
        {safeNext === "/creer-ma-fiche" ? (
          <p className="text-sm text-muted-foreground mb-5" data-testid="login-next-message">
            Pour <strong>créer ta fiche sportive</strong>, connecte-toi ou crée un compte —
            ton parcours reprendra automatiquement juste après.
          </p>
        ) : safeNext !== "/dashboard" ? (
          <p className="text-sm text-muted-foreground mb-5" data-testid="login-next-message">
            Connecte-toi pour continuer — tu seras redirigé(e) vers <code className="text-xs">{safeNext}</code> après authentification.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground mb-5">Connecte-toi pour créer ta fiche, publier des actualités et générer ton dossier sponsor.</p>
        )}

        <Button data-testid="login-google-button" variant="outline" className="w-full mb-3" onClick={googleStart} disabled={gloading}>
          {gloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Continuer avec Google
        </Button>
        <div className="relative my-4"><div className="absolute inset-0 flex items-center"><span className="w-full border-t"></span></div><div className="relative flex justify-center text-xs"><span className="bg-card px-2 text-muted-foreground">ou</span></div></div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="login" className="flex-1" data-testid="login-tab">Connexion</TabsTrigger>
            <TabsTrigger value="register" className="flex-1" data-testid="register-tab">Inscription</TabsTrigger>
          </TabsList>
          <form onSubmit={submit} className="mt-4 space-y-3">
            {tab === "register" && (
              <div><label className="text-xs">Nom</label><Input data-testid="login-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ton nom" /></div>
            )}
            <div><label className="text-xs">Email</label><Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="toi@aito.pf" /></div>
            <div><label className="text-xs">Mot de passe</label><Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></div>
            <Button data-testid="login-form-submit-button" type="submit" disabled={loading} className="w-full bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {tab === "login" ? "Connexion" : "Créer mon compte"}
            </Button>
          </form>
        </Tabs>
        <p className="mt-4 text-xs text-muted-foreground text-center">En continuant, tu acceptes les <Link to="/legal/cgu" className="underline">CGU</Link>. Consulte la <Link to="/legal/confidentialite" className="underline">Politique de confidentialité</Link> pour comprendre l'utilisation de tes données.</p>
      </Card>
    </div>
  );
}
